import React, { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const img = (slug) => `${API_BASE}/api/images/brand/${slug}`;

// Curated flip-through: cover → chapter opener → 2 recipe pages
const PAGES = [
  {
    id: "cover",
    kind: "cover",
    image: img("cover"),
    overline: "Volume One",
    title: "Mizizi",
    subtitle: "Rooted at the Table",
    caption: "A Contemporary African Cookbook",
  },
  {
    id: "chapter",
    kind: "chapter",
    image: img("chapter-grains-legumes"),
    overline: "Chapter Four",
    title: "Grains & Legumes",
    body:
      "Millet, teff, cowpeas, bambara — the quiet architecture of the African table. Slow-cooked, deeply spiced, and built for gathering.",
  },
  {
    id: "recipe-1",
    kind: "recipe",
    image: img("tamarind-glazed-sweet-potato-wedges"),
    chapter: "Starters",
    title: "Tamarind-Glazed Sweet Potato Wedges",
    meta: "Serves 4 · 25 min · Easy",
    ingredients: [
      "2 lb sweet potatoes, wedges",
      "3 tbsp tamarind paste",
      "2 tbsp date syrup",
      "1 tsp berbere",
      "Flaky salt, cilantro to finish",
    ],
    method:
      "Roast at 425°F until edges caramelize. Toss warm with tamarind, date syrup, and berbere. Finish with cilantro and flaky salt.",
  },
  {
    id: "recipe-2",
    kind: "recipe",
    image: img("peanut-braised-greens"),
    chapter: "Vegetables & Roots",
    title: "Peanut-Braised Collard Greens",
    meta: "Serves 6 · 45 min · Gentle",
    ingredients: [
      "2 bunches collards, ribbons",
      "½ cup smooth peanut butter",
      "1 red onion, sliced thin",
      "Scotch bonnet, to taste",
      "Coconut cream to finish",
    ],
    method:
      "Sweat onion until sweet. Add greens, cover, wilt. Whisk peanut butter with stock; braise 20 min. Finish with coconut cream and heat.",
  },
];

const AUTO_MS = 4200;

// Motion presets hoisted to module scope to avoid re-creating on every render
const ANIMATE_TO = { opacity: 1, x: 0 };
const TRANSITION = { duration: 0.55, ease: [0.22, 1, 0.36, 1] };
const initialFor = (dir) => ({ opacity: 0, x: dir > 0 ? 40 : -40 });
const exitFor = (dir) => ({ opacity: 0, x: dir > 0 ? -40 : 40 });

// ============================================================================
// Cover variants — flip COVER_VARIANT to try each design against the site.
//   "editorial"   → RECOMMENDED. Cream + charcoal + gold. Maximum pop against
//                   the dark forest landing hero. Editorial book-jacket feel.
//   "terracotta"  → Warm terracotta ground, cream + gold type. Feels like a
//                   fine-dining hardback. Highest warmth.
//   "framed-gold" → Cream with an inner double-gold frame, centered layout,
//                   small photo inset — the "signature edition" treatment.
// ============================================================================
const COVER_VARIANT = "editorial";

function CoverEditorial({ page }) {
  return (
    <div className="relative w-full h-full overflow-hidden bg-mizizi-cream ring-1 ring-mizizi-charcoal/10">
      {/* Top photograph — taller strip so the composition can breathe without stretching */}
      <div className="absolute inset-x-0 top-0 h-[58%] md:h-[62%] overflow-hidden bg-mizizi-forest">
        <img
          src={page.image}
          alt=""
          className="w-full h-full object-cover object-[center_30%]"
          loading="lazy"
        />
        {/* Soft cream fade at the bottom of the photo → seamless into cream lower half */}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-mizizi-cream via-mizizi-cream/70 to-transparent" />
      </div>

      {/* Lower cream half with title + serif hierarchy */}
      <div className="absolute inset-x-0 bottom-0 h-[46%] md:h-[42%] flex flex-col justify-between p-6 md:p-9 text-mizizi-charcoal">
        <div>
          <div className="overline text-mizizi-terracotta tracking-[0.35em]">{page.overline}</div>
          <div className="w-10 h-px bg-mizizi-gold mt-4 mb-3" />
          <h3 className="font-serif font-light uppercase tracking-[0.08em] text-3xl md:text-5xl leading-[0.9]">
            {page.title}
          </h3>
          <div className="font-serif italic text-mizizi-gold text-base md:text-xl mt-2">
            {page.subtitle}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-mizizi-charcoal/15">
          <div className="overline text-mizizi-charcoal/60 text-[10px]">{page.caption}</div>
          {/* Gold seal */}
          <div className="w-9 h-9 rounded-full border border-mizizi-gold flex items-center justify-center">
            <span className="overline text-mizizi-gold text-[9px] tracking-[0.15em]">I</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CoverTerracotta({ page }) {
  return (
    <div className="relative w-full h-full overflow-hidden bg-mizizi-terracotta text-mizizi-cream">
      {/* Subtle photo texture behind — low opacity so terracotta dominates */}
      <img
        src={page.image}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-multiply"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-mizizi-terracotta/60 via-mizizi-terracotta/80 to-mizizi-terracotta" />

      {/* Double thin gold frame */}
      <div className="absolute inset-3 md:inset-4 border border-mizizi-gold/70" />
      <div className="absolute inset-4 md:inset-5 border border-mizizi-gold/30" />

      <div className="relative h-full flex flex-col justify-between p-8 md:p-12">
        <div className="overline text-mizizi-gold tracking-[0.35em]">{page.overline}</div>

        <div>
          <h3 className="font-serif font-light uppercase tracking-[0.08em] text-4xl md:text-6xl leading-[0.9]">
            {page.title}
          </h3>
          <div className="w-12 h-px bg-mizizi-gold my-4" />
          <div className="font-serif italic text-mizizi-gold text-xl md:text-3xl">
            {page.subtitle}
          </div>
        </div>

        <div className="overline text-mizizi-cream/80 text-[10px] tracking-[0.3em]">{page.caption}</div>
      </div>
    </div>
  );
}

function CoverFramedGold({ page }) {
  return (
    <div className="relative w-full h-full overflow-hidden bg-mizizi-cream text-mizizi-charcoal">
      {/* Inner double gold frame */}
      <div className="absolute inset-3 md:inset-4 border-2 border-mizizi-gold" />
      <div className="absolute inset-5 md:inset-6 border border-mizizi-gold/40" />

      <div className="relative h-full flex flex-col items-center text-center p-8 md:p-12">
        <div className="overline text-mizizi-terracotta tracking-[0.4em] mt-2">{page.overline}</div>

        {/* Small photograph disc */}
        <div className="mt-6 md:mt-8 w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden ring-2 ring-mizizi-gold shadow-lg">
          <img
            src={page.image}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="mt-8 md:mt-10">
          <h3 className="font-serif font-light uppercase tracking-[0.12em] text-4xl md:text-5xl leading-[0.95]">
            {page.title}
          </h3>
          <div className="flex items-center justify-center gap-3 mt-4 text-mizizi-gold">
            <span className="h-px w-8 bg-mizizi-gold" />
            <span className="text-lg">✦</span>
            <span className="h-px w-8 bg-mizizi-gold" />
          </div>
          <div className="font-serif italic text-mizizi-gold text-xl md:text-2xl mt-3">
            {page.subtitle}
          </div>
        </div>

        <div className="mt-auto overline text-mizizi-charcoal/60 text-[10px] tracking-[0.35em]">{page.caption}</div>
      </div>
    </div>
  );
}

function PageCover({ page }) {
  if (COVER_VARIANT === "terracotta") return <CoverTerracotta page={page} />;
  if (COVER_VARIANT === "framed-gold") return <CoverFramedGold page={page} />;
  return <CoverEditorial page={page} />;
}

function PageChapter({ page }) {
  return (
    <div className="relative w-full h-full grid grid-cols-2 bg-mizizi-cream">
      <div className="relative overflow-hidden">
        <img src={page.image} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-6 md:p-10 flex flex-col justify-center text-mizizi-charcoal">
        <div className="overline text-mizizi-terracotta">{page.overline}</div>
        <h3 className="font-serif text-3xl md:text-5xl leading-tight mt-3">{page.title}</h3>
        <div className="w-10 h-px bg-mizizi-gold my-5" />
        <p className="text-sm md:text-base text-mizizi-charcoal/75 leading-relaxed">
          {page.body}
        </p>
      </div>
    </div>
  );
}

function PageRecipe({ page }) {
  return (
    <div className="relative w-full h-full grid grid-cols-5 bg-mizizi-cream">
      <div className="col-span-2 relative overflow-hidden">
        <img src={page.image} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="col-span-3 p-5 md:p-8 flex flex-col text-mizizi-charcoal overflow-hidden">
        <div className="overline text-mizizi-terracotta">{page.chapter}</div>
        <h3 className="font-serif text-xl md:text-3xl leading-tight mt-2">{page.title}</h3>
        <div className="overline text-mizizi-charcoal/50 mt-2">{page.meta}</div>
        <div className="w-8 h-px bg-mizizi-gold my-3 md:my-4" />
        <div className="overline text-mizizi-gold text-[10px] mb-2">Ingredients</div>
        <ul className="text-[11px] md:text-sm text-mizizi-charcoal/80 space-y-1 leading-snug">
          {page.ingredients.slice(0, 5).map((ing) => (
            <li key={ing} className="flex gap-2">
              <span className="text-mizizi-terracotta">·</span>
              <span>{ing}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 md:mt-4 text-[11px] md:text-xs italic text-mizizi-charcoal/60 leading-snug line-clamp-3">
          {page.method}
        </p>
      </div>
    </div>
  );
}

function renderPage(page) {
  if (page.kind === "cover") return <PageCover page={page} />;
  if (page.kind === "chapter") return <PageChapter page={page} />;
  return <PageRecipe page={page} />;
}

export default function FlipThrough({ variant = "full" }) {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const go = useCallback((next) => {
    setDir(next > idx || (idx === PAGES.length - 1 && next === 0) ? 1 : -1);
    setIdx(((next % PAGES.length) + PAGES.length) % PAGES.length);
  }, [idx]);

  const next = useCallback(() => go((idx + 1) % PAGES.length), [idx, go]);
  const prev = useCallback(() => go((idx - 1 + PAGES.length) % PAGES.length), [idx, go]);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setTimeout(() => {
      setDir(1);
      setIdx((i) => (i + 1) % PAGES.length);
    }, AUTO_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused]);

  const onKey = (e) => {
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
  };

  const aspect = variant === "compact" ? "aspect-[4/5]" : "aspect-[4/3]";

  return (
    <div
      data-testid="flipthrough"
      role="region"
      aria-label="Cookbook flip-through preview"
      tabIndex={0}
      onKeyDown={onKey}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`relative w-full ${aspect} overflow-hidden bg-mizizi-charcoal shadow-2xl select-none focus:outline-none focus:ring-2 focus:ring-mizizi-gold/60`}
    >
      {/* book spine */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-black/20 z-10 hidden md:block" />

      <AnimatePresence initial={false} custom={dir} mode="wait">
        <motion.div
          key={PAGES[idx].id}
          custom={dir}
          initial={initialFor(dir)}
          animate={ANIMATE_TO}
          exit={exitFor(dir)}
          transition={TRANSITION}
          className="absolute inset-0"
          data-testid={`flipthrough-page-${PAGES[idx].id}`}
        >
          {renderPage(PAGES[idx])}
        </motion.div>
      </AnimatePresence>

      {/* controls */}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous page"
        data-testid="flipthrough-prev"
        className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-mizizi-charcoal/50 hover:bg-mizizi-terracotta text-mizizi-cream backdrop-blur transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next page"
        data-testid="flipthrough-next"
        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-mizizi-charcoal/50 hover:bg-mizizi-terracotta text-mizizi-cream backdrop-blur transition-colors"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* dots + label */}
      <div className="absolute bottom-3 left-0 right-0 z-20 flex flex-col items-center gap-2">
        <div className="flex items-center gap-2">
          {PAGES.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`Go to page ${i + 1}`}
              data-testid={`flipthrough-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-mizizi-gold" : "w-1.5 bg-mizizi-cream/50 hover:bg-mizizi-cream/80"
              }`}
            />
          ))}
        </div>
        {variant === "full" && (
          <div className="overline text-[10px] text-mizizi-cream/70 tracking-[0.25em]">
            Peek inside · page {idx + 1} of {PAGES.length}
          </div>
        )}
      </div>
    </div>
  );
}
